import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, formatINR, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Droplet,
  Plus,
  Pencil,
  Loader2,
  Trash2,
  Upload,
  Image as ImageIcon,
  Pill,
  Printer,
  Eye,
  Download,
  FileText,
  MessageCircle,
  MoreHorizontal,
  CalendarPlus,
  ListOrdered,
  Receipt,
  IndianRupee,
  History as HistoryIcon,
} from "lucide-react";
import { toast } from "sonner";
import { PatientDialog } from "./Patients";
import PrescriptionDialog from "../components/PrescriptionDialog";
import FollowUpDialog from "../components/FollowUpDialog";
import QuickBillDialog from "../components/QuickBillDialog";
import { openWhatsApp, TEMPLATES } from "../lib/whatsapp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import PrescriptionView from "../components/PrescriptionView";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [rxDialogOpen, setRxDialogOpen] = useState(false);
  const [rxViewOpen, setRxViewOpen] = useState(false);
  const [selectedRx, setSelectedRx] = useState(null);
  const [activeTab, setActiveTab] = useState("visits");
  const [followUpFor, setFollowUpFor] = useState(null); // patient passed when prompt opens
  const [clinicName, setClinicName] = useState("our clinic");
  const [defaultFee, setDefaultFee] = useState(500);
  const [upiVpa, setUpiVpa] = useState("");
  const [upiPayeeName, setUpiPayeeName] = useState("");
  const [billFor, setBillFor] = useState(null); // {mode}

  // Fetch clinic name + default fee
  useEffect(() => {
    api.get("/clinic/settings")
      .then(({ data }) => {
        setClinicName(data?.name || "our clinic");
        if (data?.default_consultation_fee != null) setDefaultFee(data.default_consultation_fee);
        setUpiVpa(data?.upi_vpa || "");
        setUpiPayeeName(data?.upi_payee_name || data?.name || "");
      })
      .catch(() => {});
  }, []);

  // FAB → "New visit + invoice" event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.action === "newVisit") setVisitOpen(true);
    };
    window.addEventListener("hedesk:fab", handler);
    return () => window.removeEventListener("hedesk:fab", handler);
  }, []);

  const whatsappPatient = () => {
    if (!data?.patient?.phone) {
      toast.error("Patient phone is missing");
      return;
    }
    const msg = TEMPLATES.generalMessage({
      patient_name: data.patient.name,
      clinic_name: clinicName,
    });
    openWhatsApp(data.patient.phone, msg);
  };

  const quickBookAppointment = () => {
    if (!data?.patient) return;
    navigate("/appointments", { state: { openBook: true, patient_id: data.patient.patient_id } });
  };

  const quickAddToQueue = async () => {
    if (!data?.patient) return;
    try {
      const { data: doctors } = await api.get("/doctors");
      if (!doctors?.length) { toast.error("No doctors available"); return; }
      const { data: apt } = await api.post("/appointments/walk-in", {
        patient_id: data.patient.patient_id,
        doctor_id: doctors[0].user_id,
        reason: "Walk-in",
      });
      toast.success(`Added to queue · Token #${apt.queue_number}`);
      navigate("/queue");
    } catch (e) {
      toast.error("Could not add to queue");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Patient not found");
      navigate("/patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const deletePatient = async () => {
    if (!confirm("Delete this patient and all visit history?")) return;
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Patient deleted");
      navigate("/patients");
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { patient, visits, prescriptions = [] } = data;

  const openRxView = (rx) => {
    setSelectedRx(rx);
    setRxViewOpen(true);
  };

  const deleteRx = async (id) => {
    if (!confirm("Delete this prescription?")) return;
    // Optimistic update — remove immediately from local state so card disappears
    setData((prev) => prev ? { ...prev, prescriptions: prev.prescriptions.filter((r) => r.prescription_id !== id) } : prev);
    try {
      await api.delete(`/prescriptions/${id}`);
      toast.success("Prescription deleted");
    } catch (e) {
      toast.error("Failed");
      load(); // refetch on error to restore
    }
  };

  const downloadRxFile = (id, fmt) => {
    const url = `${BACKEND_URL}/api/prescriptions/${id}/${fmt}`;
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `rx-${id.slice(-8)}.${fmt}`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error(`${fmt.toUpperCase()} download failed`));
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto fade-in" data-testid="patient-detail-page">
      <Link to="/patients" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> All patients
      </Link>

      {/* Patient header */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 text-white flex items-center justify-center text-2xl font-semibold">
              {patient.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }} data-testid="patient-name">
                {patient.name}
              </h1>
              <div className="text-sm text-slate-500">
                {patient.age} years · <span className="capitalize">{patient.gender}</span>
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-700">
                <div className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" /> {patient.phone}</div>
                {patient.blood_group && (
                  <div className="flex items-center gap-1.5"><Droplet className="w-4 h-4 text-red-400" /> {patient.blood_group}</div>
                )}
                {patient.address && (
                  <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {patient.address}</div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={whatsappPatient} className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid="whatsapp-patient-btn" title="Open WhatsApp">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="patient-quick-actions-trigger">
                    <MoreHorizontal className="w-4 h-4 mr-2" /> Quick actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="patient-quick-actions-menu">
                  <DropdownMenuLabel>Workflow shortcuts</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={quickBookAppointment} data-testid="qa-book-appointment">
                    <CalendarPlus className="w-4 h-4 mr-2 text-blue-600" /> Book appointment
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={quickAddToQueue} data-testid="qa-add-to-queue">
                    <ListOrdered className="w-4 h-4 mr-2 text-purple-600" /> Add to queue
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRxDialogOpen(true)} data-testid="qa-create-rx">
                    <Pill className="w-4 h-4 mr-2 text-indigo-600" /> Create prescription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBillFor({ mode: "bill" })} data-testid="qa-generate-bill">
                    <Receipt className="w-4 h-4 mr-2 text-amber-600" /> Generate bill
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBillFor({ mode: "paid" })} data-testid="qa-mark-paid">
                    <IndianRupee className="w-4 h-4 mr-2 text-emerald-600" /> Mark paid
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFollowUpFor(data?.patient)} data-testid="qa-follow-up">
                    <CalendarPlus className="w-4 h-4 mr-2 text-sky-600" /> Schedule follow-up
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setActiveTab("visits")} data-testid="qa-view-history">
                    <HistoryIcon className="w-4 h-4 mr-2 text-slate-600" /> View history
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-patient-btn">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
              <Button variant="outline" onClick={deletePatient} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-patient-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="visits" data-testid="tab-visits">
            <FileText className="w-4 h-4 mr-1.5" /> Visits ({visits.length})
          </TabsTrigger>
          <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">
            <Pill className="w-4 h-4 mr-1.5" /> Prescriptions ({prescriptions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="visits">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>
              Visit history
            </h2>
            <Button onClick={() => setVisitOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-visit-btn">
              <Plus className="w-4 h-4 mr-2" /> New visit
            </Button>
          </div>

          {visits.length === 0 ? (
            <Card className="border-dashed border-slate-300 bg-slate-50/40">
              <CardContent className="p-10 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No visits recorded yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {visits.map((v) => (
                <Card key={v.visit_id} className="border-slate-200" data-testid={`visit-${v.visit_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {new Date(v.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                        </div>
                        <div className="text-xs text-slate-500">By {v.doctor_name}</div>
                      </div>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {formatINR(v.fee)}
                      </Badge>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Symptoms</div>
                        <div className="text-slate-700">{v.symptoms || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Diagnosis</div>
                        <div className="text-slate-700">{v.diagnosis || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Prescription</div>
                        <div className="text-slate-700 whitespace-pre-wrap">{v.prescription || "—"}</div>
                      </div>
                    </div>
                    {v.prescription_image_path && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <PrescriptionImage fileId={v.prescription_image_path} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="prescriptions">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>
              Prescriptions
            </h2>
            <Button onClick={() => setRxDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-prescription-btn">
              <Plus className="w-4 h-4 mr-2" /> New prescription
            </Button>
          </div>

          {prescriptions.length === 0 ? (
            <Card className="border-dashed border-slate-300 bg-slate-50/40">
              <CardContent className="p-10 text-center">
                <Pill className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No prescriptions yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {prescriptions.map((rx) => (
                <Card
                  key={rx.prescription_id}
                  className="border-slate-200 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                  onClick={() => openRxView(rx)}
                  data-testid={`prescription-${rx.prescription_id}`}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {rx.date || new Date(rx.created_at).toISOString().slice(0, 10)}
                        </div>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          {rx.medicines?.length || 0} meds
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-700 font-medium truncate">{rx.diagnosis || "Prescription"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">By {rx.doctor_name}</div>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openRxView(rx)} data-testid={`view-rx-${rx.prescription_id}`} title="View & print">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadRxFile(rx.prescription_id, "pdf")} data-testid={`pdf-rx-${rx.prescription_id}`} title="Download PDF">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadRxFile(rx.prescription_id, "docx")} data-testid={`docx-rx-${rx.prescription_id}`} title="Download DOCX">
                        <FileText className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteRx(rx.prescription_id)} data-testid={`delete-rx-${rx.prescription_id}`} title="Delete">
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <PatientDialog open={editOpen} onOpenChange={setEditOpen} initial={patient} onSaved={load} />
      <VisitDialog
        open={visitOpen}
        onOpenChange={setVisitOpen}
        patientId={patient.patient_id}
        onSaved={load}
      />
      <PrescriptionDialog
        open={rxDialogOpen}
        onOpenChange={setRxDialogOpen}
        patientId={patient.patient_id}
        doctorName={user?.name || ""}
        onSaved={(savedRx, ctx) => {
          setActiveTab("prescriptions");
          load();
          // Prompt follow-up only after creating a brand-new prescription
          if (savedRx && !ctx?.isEdit) {
            setFollowUpFor(patient);
          }
        }}
      />
      <FollowUpDialog
        open={!!followUpFor}
        onOpenChange={(o) => { if (!o) setFollowUpFor(null); }}
        patient={followUpFor}
        doctorId={user?.user_id}
        clinicName={clinicName}
      />
      <QuickBillDialog
        open={!!billFor}
        onOpenChange={(o) => { if (!o) setBillFor(null); }}
        patient={data?.patient}
        mode={billFor?.mode || "bill"}
        defaultAmount={defaultFee}
        upiVpa={upiVpa}
        upiPayeeName={upiPayeeName}
        onCompleted={(_inv, opts) => {
          if ((billFor?.mode === "paid" || opts?.paid)) setFollowUpFor(data?.patient);
          if (!opts?.keepOpen) setBillFor(null);
        }}
      />
      <PrescriptionView
        open={rxViewOpen}
        onOpenChange={setRxViewOpen}
        prescription={selectedRx}
        patient={patient}
      />
    </div>
  );
}

function PrescriptionImage({ fileId }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const resp = await api.get(`/files/${fileId}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        revoke = url;
        setBlobUrl(url);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId]);

  if (!blobUrl) {
    return <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading prescription...</div>;
  }
  return (
    <a href={blobUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
      <img src={blobUrl} alt="prescription" className="max-h-48 rounded-lg border border-slate-200" />
    </a>
  );
}

function VisitDialog({ open, onOpenChange, patientId, onSaved }) {
  const [form, setForm] = useState({
    symptoms: "",
    diagnosis: "",
    prescription: "",
    fee: "500",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ symptoms: "", diagnosis: "", prescription: "", fee: "500" });
      setFile(null);
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      let prescription_image_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const upRes = await api.post("/upload/prescription", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        prescription_image_path = upRes.data.file_id;
      }
      await api.post("/visits", {
        patient_id: patientId,
        symptoms: form.symptoms,
        diagnosis: form.diagnosis,
        prescription: form.prescription,
        fee: parseFloat(form.fee),
        prescription_image_path,
      });
      toast.success("Visit recorded. Invoice generated.");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="visit-dialog" className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>New visit</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Symptoms</Label>
            <Textarea value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} placeholder="What is the patient experiencing?" rows={2} className="mt-1.5" data-testid="visit-symptoms-input" />
          </div>
          <div>
            <Label>Diagnosis</Label>
            <Textarea value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} placeholder="Doctor's diagnosis" rows={2} className="mt-1.5" data-testid="visit-diagnosis-input" />
          </div>
          <div>
            <Label>Prescription</Label>
            <Textarea value={form.prescription} onChange={(e) => setForm({ ...form, prescription: e.target.value })} placeholder="Medicines, dosage, instructions..." rows={3} className="mt-1.5" data-testid="visit-prescription-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Consultation fee (₹)</Label>
              <Input type="number" min="0" step="50" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} required className="mt-1.5" data-testid="visit-fee-input" />
            </div>
            <div>
              <Label>Prescription image (optional)</Label>
              <div className="mt-1.5">
                <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="visit-file-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="w-full justify-start">
                  {file ? (
                    <><ImageIcon className="w-4 h-4 mr-2 text-emerald-600" /> {file.name.slice(0, 20)}</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Choose file</>
                  )}
                </Button>
              </div>
            </div>
          </div>
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="visit-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save visit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
